import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { getChecklistMappings, setChecklistMapping, deleteChecklistMapping, getChecklistTemplates } from 'api/canvms/incidents';
import type { ChecklistMapping, ChecklistTemplate } from '../../types/canvision.types';

interface ChecklistMappingSettingsProps {
  open: boolean;
  onClose: () => void;
}

function ChecklistMappingSettings({ open, onClose }: ChecklistMappingSettingsProps) {
  const [mappings, setMappings] = useState<ChecklistMapping[]>([]);
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<ChecklistMapping | null>(null);
  const [formIncidentType, setFormIncidentType] = useState('');
  const [formTemplateId, setFormTemplateId] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [mappingsRes, templatesRes] = await Promise.all([
        getChecklistMappings(),
        getChecklistTemplates(),
      ]);
      setMappings(mappingsRes.data);
      setTemplates(templatesRes.data);
    } catch (err: any) {
      console.error('[CANVMS/ChecklistMappingSettings] Error fetching data:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, fetchData]);

  const handleAdd = () => {
    setEditingMapping(null);
    setFormIncidentType('');
    setFormTemplateId('');
    setDialogOpen(true);
  };

  const handleEdit = (mapping: ChecklistMapping) => {
    setEditingMapping(mapping);
    setFormIncidentType(mapping.incident_type);
    setFormTemplateId(mapping.checklist_template_id);
    setDialogOpen(true);
  };

  const handleDelete = async (incidentType: string) => {
    try {
      await deleteChecklistMapping(incidentType);
      setMappings((prev) => prev.filter((m) => m.incident_type !== incidentType));
      setNotification({ type: 'success', message: `Removed mapping for "${incidentType}"` });
    } catch (err: any) {
      setNotification({ type: 'error', message: err.response?.data?.message || 'Failed to delete mapping' });
    }
  };

  const handleSave = async () => {
    if (!formIncidentType.trim() || !formTemplateId) return;

    try {
      setSaving(true);
      const result = await setChecklistMapping(formIncidentType.trim(), formTemplateId as number);
      // Update local state
      setMappings((prev) => {
        const existing = prev.findIndex((m) => m.incident_type === formIncidentType.trim());
        const template = templates.find((t) => t.ChecklistID === formTemplateId);
        const updated: ChecklistMapping = {
          ...result.data,
          template_name: template?.Name || null,
        };
        if (existing >= 0) {
          const newMappings = [...prev];
          newMappings[existing] = updated;
          return newMappings;
        }
        return [...prev, updated];
      });
      setDialogOpen(false);
      setNotification({
        type: 'success',
        message: editingMapping ? 'Mapping updated' : 'Mapping created',
      });
    } catch (err: any) {
      setNotification({ type: 'error', message: err.response?.data?.message || 'Failed to save mapping' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 520, md: 600 }, maxWidth: '100%' },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="h6">Incident Checklist Mappings</Typography>
            <Typography variant="body2" color="text.secondary">
              Assign a checklist template to each incident type. When a patrol is dispatched, the template is cloned for the guard.
            </Typography>
          </Box>
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 2, overflow: 'auto', flex: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAdd}
                  disabled={templates.length === 0}
                >
                  Add Mapping
                </Button>
              </Box>

              {templates.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No checklist templates found. Create a template via the Checklist management screens first.
                </Alert>
              )}

              {mappings.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No incident type mappings configured yet.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Incident Type</TableCell>
                      <TableCell>Checklist Template</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mappings.map((mapping) => (
                      <TableRow key={mapping.id}>
                        <TableCell>
                          <Chip label={mapping.incident_type} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          {mapping.template_name || `Template #${mapping.checklist_template_id}`}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleEdit(mapping)} title="Edit">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(mapping.incident_type)}
                            title="Remove"
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </Box>
      </Drawer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingMapping ? 'Edit Mapping' : 'Add Mapping'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Incident Type"
            value={formIncidentType}
            onChange={(e) => setFormIncidentType(e.target.value)}
            fullWidth
            margin="normal"
            disabled={!!editingMapping}
            placeholder="e.g. person, vehicle, motion"
            helperText="The incident type label that triggers this checklist"
          />
          <TextField
            select
            label="Checklist Template"
            value={formTemplateId}
            onChange={(e) => setFormTemplateId(Number(e.target.value))}
            fullWidth
            margin="normal"
          >
            {templates.map((t) => (
              <MenuItem key={t.ChecklistID} value={t.ChecklistID}>
                {t.Name} ({t.checkpoint_count} checkpoints)
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !formIncidentType.trim() || !formTemplateId}
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {notification ? (
          <Alert severity={notification.type} onClose={() => setNotification(null)}>
            {notification.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}

export default ChecklistMappingSettings;
